// URL model — language prefix is the single source of truth (best-practice §6).
//   en classic -> /            es classic -> /es
//   en emoji   -> /emoji       es emoji   -> /es/emoji
// English has no prefix; the main mode (classic) has no path segment.

export const LANGS = ['en', 'es', 'pt', 'ja'] as const;
export type Lang = (typeof LANGS)[number];
export const DEFAULT_LANG: Lang = 'en';
export const ORIGIN = 'https://animedle.today';

export interface ModeDef {
  key: string;
  icon: string;
  enabled: boolean;
  maxGuesses?: number; // omit = unlimited (attribute modes are ∞, best-practice §7.3)
}

export const MODES: ModeDef[] = [
  { key: 'classic', icon: '作', enabled: true },
  { key: 'character', icon: '人', enabled: true },
  { key: 'pixel', icon: 'PX', enabled: true },
  { key: 'emoji', icon: '絵', enabled: false },
  { key: 'endless', icon: '∞', enabled: false },
];

// null = unlimited guesses (no lose state).
export function maxGuessesFor(mode: string): number | null {
  return MODES.find((m) => m.key === mode)?.maxGuesses ?? null;
}

export const DEFAULT_MODE = 'classic';
export const ENABLED_MODES = MODES.filter((m) => m.enabled).map((m) => m.key);

const LANG_SET = new Set<string>(LANGS);
// Only enabled modes resolve to a real page; unknown/disabled fall back to classic.
const MODE_SET = new Set(MODES.filter((m) => m.enabled).map((m) => m.key));

export function isLang(s: string): s is Lang {
  return LANG_SET.has(s);
}

export function pathFor(lang: Lang, mode: string): string {
  const parts: string[] = [];
  if (lang !== DEFAULT_LANG) parts.push(lang);
  if (mode !== DEFAULT_MODE) parts.push(mode);
  return '/' + parts.join('/');
}

export interface Route {
  lang: Lang;
  mode: string;
}

export function parsePath(pathname: string): Route {
  const tokens = pathname.split('/').filter(Boolean);
  let lang: Lang = DEFAULT_LANG;
  let i = 0;
  if (tokens[i] && isLang(tokens[i]) && tokens[i] !== DEFAULT_LANG) {
    lang = tokens[i] as Lang;
    i++;
  }
  let mode = DEFAULT_MODE;
  if (tokens[i] && MODE_SET.has(tokens[i])) mode = tokens[i];
  return { lang, mode };
}

// All (lang, enabled-mode) pairs — used by prerender and sitemap.
export function allRoutes(): Route[] {
  const out: Route[] = [];
  for (const lang of LANGS) for (const mode of ENABLED_MODES) out.push({ lang, mode });
  return out;
}
